import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  styled,
  Theme,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Icon } from './icon';

export interface DialogTitleProps {
  children?: React.ReactNode;
  onClose: (event?: Event) => void;
}

export type ModalProps = Omit<DialogProps, 'onClose'> & {
  onClose: (event?: Event) => void;
  paperMaxWidth?: string;
};

const CustomDialog = styled(Dialog)<ModalProps>`
  & .MuiDialogContent-root {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    padding-bottom: 1.5rem;
    margin-top: 16px;
  }
  & .MuiDialogTitle-root {
    padding: 3.5rem 0 0;
  }
  & .MuiDialog-paper {
    border-radius: 10px;
    padding-right: 2rem;
    padding-left: 0.5rem;
    background-color: #0e1520;
    background-image: none;
    max-width: ${({ paperMaxWidth }) => `${paperMaxWidth ?? '1128px'}`};

    @media (max-width: ${({ theme }: { theme: Theme }) =>
        theme.breakpoints.values.lg}px) {
      border-radius: 0px;
      width: 100%;
      height: 100%;
      max-height: 100vh;
      max-width: 100vw;
      margin-right: 0;
      margin-left: 0;
      margin-bottom: 0;
      padding-top: 8rem;
      padding-bottom: 0;
    }
    @media (max-width: ${({ theme }: { theme: Theme }) =>
        theme.breakpoints.values.sm}px) {
      margin-left: 0;
      margin-right: 0;
      width: 100%;
      padding-right: 2rem;
      padding-top: 6rem;
    }
  }
  .modalRoot {
    backdrop-filter: blur(150px);
    background-image: linear-gradient(
      180deg,
      rgba(6, 7, 10, 0.5) 0%,
      rgba(26, 53, 93, 0.5) 198.49%
    );
  }
`;

const CustomIconButton = styled(IconButton)`
  position: absolute;
  right: 1.5rem;
  top: 1.5rem;
  color: ${({ theme }: { theme: Theme }) => theme.palette.greyscale['05']};
  @media (max-width: ${({ theme }: { theme: Theme }) =>
      theme.breakpoints.values.lg}px) {
    right: 1.5rem;
    top: 9rem;
  }
  @media (max-width: ${({ theme }: { theme: Theme }) =>
      theme.breakpoints.values.sm}px) {
    right: 0.75rem;
    top: 7rem;
  }
`;

export const ModalTitle = styled('h1')`
  font-family: Roboto Mono;
  text-transform: uppercase;
  font-size: 24px;
  line-height: 40px;
  margin-bottom: 2rem;
  margin-top: 1rem;
  @media (max-width: ${({ theme }: { theme: Theme }) =>
      theme.breakpoints.values.sm}px) {
    line-height: 36px;
  }
`;

const CustomDialogTitle = (props: DialogTitleProps) => {
  const { children, onClose, ...other } = props;
  const handleClose = () => onClose();
  return (
    <DialogTitle {...other}>
      {children}
      <CustomIconButton aria-label="close" onClick={handleClose}>
        <Icon iconName="close-circle-outline" size="medium" />
      </CustomIconButton>
    </DialogTitle>
  );
};

export const Modal: React.FC<
  ModalProps & {
    title?: string;
    style?: object;
  }
> = ({ open, onClose, title, children, style, paperMaxWidth }) => {
  const theme = useTheme();
  const clickContentBackgroundToClose = useMediaQuery(
    `(max-width: ${theme.breakpoints.values.md}px)`,
  );
  return (
    <CustomDialog
      className="customDialog"
      fullWidth
      maxWidth="lg"
      onClose={onClose}
      open={open}
      scroll="paper"
      theme={theme}
      BackdropProps={{
        classes: {
          root: 'modalRoot',
        },
      }}
      sx={{
        zIndex: 50,
      }}
      style={style}
      paperMaxWidth={paperMaxWidth}
    >
      <CustomDialogTitle onClose={onClose} />
      <DialogContent
        onClick={clickContentBackgroundToClose ? () => onClose() : () => {}}
      >
        {title && <ModalTitle>{title}</ModalTitle>}
        {children}
      </DialogContent>
    </CustomDialog>
  );
};

export default Modal;
